import { useEffect, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DatePickerField } from "@/components/ui/date-picker-field";
import { TimePickerField } from "@/components/ui/time-picker-field";
import { Loader2, CalendarClock } from "lucide-react";
import { toLocalYMD } from "@/lib/validation";

interface RescheduleProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (startTimeIso: string) => Promise<void>;
  loading?: boolean;
  title?: string;
  description?: string;
  /** The event's currently scheduled start time, to pre-fill the pickers instead of opening blank. */
  initialStart?: Date | null;
}

/** Modal for picking a new date/time and PATCHing the existing calendar event to match. */
export function RescheduleEventDialog({ open, onClose, onConfirm, loading, title, description, initialStart }: RescheduleProps) {
  const [date, setDate] = useState<Date | undefined>();
  const [time, setTime] = useState("10:00");
  // Only re-seed on the closed→open transition. `initialStart` is a fresh
  // Date object on every parent render (even unrelated ones, e.g. while
  // `loading` toggles), so keying off it directly would blow away whatever
  // the user just picked the moment the parent re-renders mid-submit.
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      if (initialStart) {
        setDate(initialStart);
        setTime(`${String(initialStart.getHours()).padStart(2, "0")}:${String(initialStart.getMinutes()).padStart(2, "0")}`);
      } else {
        setDate(undefined);
        setTime("10:00");
      }
    }
    wasOpen.current = open;
  }, [open, initialStart]);

  async function handleConfirm() {
    if (!date) return;
    const iso = new Date(`${toLocalYMD(date)}T${time}:00`).toISOString();
    await onConfirm(iso);
  }

  return (
    <Dialog open={open} onOpenChange={v => { if (!v && !loading) { setDate(undefined); onClose(); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-primary" />
            {title ?? "Reschedule event"}
          </DialogTitle>
          <DialogDescription>
            {description ?? "Pick a new date and time. The existing calendar event will be updated in place."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Date</Label>
            <DatePickerField value={date} onChange={setDate} placeholder="Select date" disabled={d => d < new Date(new Date().toDateString())} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Time</Label>
            <TimePickerField value={time} onChange={setTime} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={loading || !date} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
            Save new time
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DeleteProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  loading?: boolean;
  title?: string;
  description?: string;
  confirmLabel?: string;
}

/** Confirm-before-delete for a synced calendar event (or an action that also removes one). */
export function DeleteEventDialog({ open, onClose, onConfirm, loading, title, description, confirmLabel }: DeleteProps) {
  return (
    <AlertDialog open={open} onOpenChange={v => { if (!v && !loading) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title ?? "Delete this calendar event?"}</AlertDialogTitle>
          <AlertDialogDescription>
            {description ?? "This removes the event from Google Calendar. This action cannot be undone."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Go back</AlertDialogCancel>
          <AlertDialogAction
            onClick={e => { e.preventDefault(); onConfirm(); }}
            disabled={loading}
            className="gap-2"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel ?? "Delete event"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
